/* eslint-disable import/no-cycle */
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  Signal,
  computed,
  effect,
  inject,
  input,
  runInInjectionContext,
  signal,
} from '@angular/core'

import {
  FlxPositionStyleDirective,
  FlxStyleModule,
  FlxThemeModule,
  FlxWrapperStyleDirective,
} from '@flowx/angular-theme'
import {
  ContainerTemplateConfig,
  FooterTemplateConfig,
  HeaderTemplateConfig,
  ZoneTemplateConfig,
  isContainerConfig,
  isUserTaskRoot,
  isZoneConfig,
} from '@flowx/core-sdk'

import { FlxTreeNodeComponent } from '../../renderer/tree-node/tree-node.component'
import { FlxActionService } from '../../services/flx-action.service'
import { emitScreenAnalyticsOnDisplay } from '../../utils/screen-analytics.utils'
import { getBoolExpressionSignal } from '../helpers'

@Component({
  selector: 'flx-container',
  hostDirectives: [
    { directive: FlxWrapperStyleDirective, inputs: ['flxWrapperStyle: templateConfig'] },
    { directive: FlxPositionStyleDirective, inputs: ['flxPositionStyle: templateConfig'] },
  ],
  imports: [CommonModule, FlxTreeNodeComponent, FlxThemeModule, FlxStyleModule],
  templateUrl: './flx-container.component.html',
  styleUrl: './flx-container.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class]': `displayOptions().className`,
    '[attr.role]': 'landmark()',
    '[attr.id]': 'linkId()',
  },
})
export class FlxContainerComponent implements OnInit, AfterViewInit {
  #actionService = inject(FlxActionService)
  #destroyRef = inject(DestroyRef)
  #injector = inject(Injector)
  #flxWrapperStyle = inject(FlxWrapperStyleDirective)

  templateConfig = input.required<
    ContainerTemplateConfig | ZoneTemplateConfig | HeaderTemplateConfig | FooterTemplateConfig
  >()

  displayOptions = computed(() => this.templateConfig().displayOptions)
  flowxProps = computed(() => this.displayOptions().flowxProps)
  style = computed(() => this.displayOptions().style)
  landmark = computed(() => this.flowxProps()?.accessibility?.role)
  // linkId is a runtime property added during rendering, not in type definitions
  linkId = computed(() => (this.templateConfig() as { linkId?: string }).linkId)

  evaluatedConditionalStyles = signal([])
  conditionalStyles = computed(() =>
    this.evaluatedConditionalStyles()
      .filter(({ expression }) => expression())
      .map(({ overrides }) => overrides)
  )

  // expressions
  isHidden: Signal<boolean>

  ngOnInit(): void {
    const config = this.templateConfig()

    // Trigger onLoad for root zones (ZONE component identifier only), with optional load workflow
    this.#actionService.runOnLoadEvent(
      config,
      this.#destroyRef,
      (c) => isZoneConfig(c) && isUserTaskRoot(c)
    )

    // Trigger onDisplay for root zones (ZONE component identifier only)
    this.#actionService.runOnDisplayEvent(
      config,
      this.#destroyRef,
      (c) => isZoneConfig(c) && isUserTaskRoot(c)
    )

    // Analytics and hide are only available on Container configs
    if (isContainerConfig(config)) {
      const containerProps = config.displayOptions.flowxProps

      emitScreenAnalyticsOnDisplay(config, containerProps, this.#destroyRef)

      runInInjectionContext(this.#injector, () => {
        const { hide } = containerProps
        this.isHidden = getBoolExpressionSignal(hide, config, { logType: 'hide' })

        const containerStyle = config.displayOptions.style
        const conditionals = containerStyle?.conditionals
        if (Array.isArray(conditionals)) {
          this.evaluatedConditionalStyles.set(
            conditionals.map(({ condition, overrides }, index) => ({
              expression: getBoolExpressionSignal(condition, config, {
                logType: 'conditional',
                conditionIndex: index,
              }),
              overrides,
            }))
          )
        }
      })
    } else {
      // Zone config - no hide expression, always visible
      runInInjectionContext(this.#injector, () => {
        this.isHidden = signal(false) as Signal<boolean>
      })
    }
  }

  ngAfterViewInit(): void {
    runInInjectionContext(this.#injector, () => {
      effect(() => {
        this.#flxWrapperStyle.toggleDisplay(this.isHidden())
      })
    })
  }
}
